import React, { ChangeEvent, ReactNode, useEffect, useMemo } from "react";

import {
  faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { Grid } from "@material-ui/core";
import isArray from "lodash/isArray";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import styled from "styled-components/macro";

import {
  BillingSettingsAction,
  IntegrationCredentialAction,
  XeroAccountingSettingsAction,
} from "actions";

import { AdminLink } from "components/AdminLink/AdminLink";
import { PaddedPaper } from "components/Paper";
import { MultiButton } from "components/Button";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { LargeFaIcon, SmallFaIcon } from "components/FaIcon";
import { Button, SecondaryButton } from "components/Form";
import { CheckBoxBase } from "components/Form/FormikControls";
import { CenteredGreedy, Column, Row } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import MessageBox from "components/MessageBox";
import { TimeSince } from "components/TimeSince";

import {
  IntegrationType,
  XeroSyncDatapoint,
  XeroSyncDatapointLabels,
} from "constants/integrations";
import { ApiModel } from "constants/loading";

import { ForLivestockAgent } from "containers/ForUserType";

import { formatUTCToLocalDateTimeString } from "lib/timeFormats";

import {
  getBillingSettings,
  getIntegrationCredentials,
  getIntegrationCredentialById,
  selectIntegrationCredentialIdsByTypeLookup,
} from "selectors";

import { useBoolean, useCanLinkExternalIntegrationPermission } from "hooks";
import XeroConnect from "img/xero/connect-blue.svg";
import XeroDisconnect from "img/xero/disconnect-blue.svg";

const Wrapper = styled.div`
  padding: ${({ theme }) => theme.space[3]}px;
`;

const XeroHelp = () => {
  return (
    <p>
      Xero is an accounting package that can be integrated into AgriNous.
      Enabling the integration will allow you to match Xero businesses to
      AgriNous businesses facilitating seamless billing exports.
      <br />
      Creating a new integration, or reactivating an existing integration will
      redirect you to Xero where you will need to login and authorize
      AgriNous&apos; access to your organization.
    </p>
  );
};

const IntegrationTypeHelpMap: Record<IntegrationType, ReactNode> = {
  [IntegrationType.Xero]: <XeroHelp />,
};

const IntegrationTypeDisconnectMap: Record<IntegrationType, ReactNode> = {
  [IntegrationType.Xero]: (
    //   Maintain original ratio, but scale to a set 48px height. (222 / 43) * 48 (height) = 248
    <img src={XeroDisconnect} width={248} height={48} alt="Disconnect Xero" />
  ),
};

interface SynchroniseModalProps {
  integrationCredentialId: string;
  isOpen: boolean;
  onClose: () => void;
}

const SynchroniseModal = ({
  integrationCredentialId,
  isOpen,
  onClose,
}: SynchroniseModalProps) => {
  const dispatch = useDispatch();

  const syncDatapoint = (dataPoint: XeroSyncDatapoint) => () => {
    dispatch(
      IntegrationCredentialAction.synchronize(
        integrationCredentialId,
        dataPoint,
      ),
    );
    onClose();
  };

  const syncDocuments = () => {
    dispatch(
      IntegrationCredentialAction.syncDocuments(integrationCredentialId),
    );
    onClose();
  };

  const syncAll = () => {
    dispatch(IntegrationCredentialAction.synchronize(integrationCredentialId));
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose}>Synchronise with Xero</DialogTitle>
      <DialogContent dividers>
        <Column>
          <p>
            Choose what you would like to synchronise with Xero. Each
            synchronisation can make a large number of requests to Xero,
            particularly for organisations with many businesses or documents.
            Syncing frequently, or syncing everything when it isn&apos;t needed,
            may cause Xero to rate limit AgriNous, which can delay
            synchronisation across the platform.
          </p>
          {Object.values(XeroSyncDatapoint).map(dataPoint => (
            <Row justifyEnd alignCenter padding={1} key={dataPoint}>
              <Button onClick={syncDatapoint(dataPoint)}>
                {XeroSyncDatapointLabels[dataPoint]}
              </Button>
            </Row>
          ))}
          <Row justifyEnd alignCenter padding={1}>
            <Button onClick={syncDocuments}>Sync Documents</Button>
          </Row>
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
        <Button onClick={syncAll}>Sync All</Button>
      </DialogActions>
    </Dialog>
  );
};

interface IntegrationCredentialCardProps {
  id: string;
}

interface IntegrationCredentialWithSyncState extends IntegrationCredential {
  syncing?: boolean;
}

interface XeroTenant {
  tenantId: string;
  tenantName: string;
}

interface TenantOption {
  value: string;
  label: string;
}

const IntegrationCredentialCard = ({ id }: IntegrationCredentialCardProps) => {
  const integrationCredential = useSelector(
    getIntegrationCredentialById(id),
  ) as IntegrationCredentialWithSyncState;
  const tenants = useMemo<XeroTenant[]>(() => {
    try {
      const data = integrationCredential.tenantBlob
        ? JSON.parse(integrationCredential.tenantBlob)
        : [];
      if (!isArray(data)) {
        return [];
      }
      return data;
    } catch (e) {
      // This gets redacted in developer data, so return an empty array.
      return [];
    }
  }, [integrationCredential.tenantBlob]);

  const tenantOptions: TenantOption[] = tenants.map(tenant => {
    return { value: tenant.tenantId, label: tenant.tenantName };
  });
  const [tenantId, setTenantId] = React.useState(
    integrationCredential.defaultTenant,
  );
  const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] =
    useBoolean(false);
  const [isSynchroniseOpen, openSynchronise, closeSynchronise] =
    useBoolean(false);

  const dispatch = useDispatch();

  const onDelete = () => {
    dispatch(IntegrationCredentialAction.delete(id));
    closeConfirmDelete();
  };

  const reactivate = () => {
    dispatch(IntegrationCredentialAction.activate(id, tenantId));
  };

  const updateTenant = () => {
    dispatch(
      IntegrationCredentialAction.update(
        {
          id,
          defaultTenant: tenantId,
          isEnabled: true,
          message: null,
        },
        { changeReason: "Updated selected Tenant" },
      ),
    );
  };

  const setIngestExternalDocument = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    dispatch(
      IntegrationCredentialAction.update(
        {
          id,
          ingestExternalDocuments: newValue,
        },
        { changeReason: `Set Ingest External Documents to ${newValue}` },
      ),
    );
  };

  const confirmMessage = (
    <div>
      This will remove the integration from AgriNous and remove any related data
      that may have been connected, including:
      <ul>
        <li>All Business Mappings</li>
        {/* <li>All Mapped Invoices</li> */}
      </ul>
    </div>
  );

  return (
    <>
      <PaddedPaper spacing={3} marginBottom={3} elevation={2}>
        <Grid container>
          <Grid item xs={12}>
            <h3>
              {integrationCredential.organizationName}
              <AdminLink
                app="agrinous_external_integration"
                model="integrationcredential"
                cbid={integrationCredential.id}
              />
            </h3>
          </Grid>

          {integrationCredential.syncing ? (
            <CenteredGreedy>
              <LoadingSpinner size={undefined} subjectName="" />
            </CenteredGreedy>
          ) : (
            <>
              <Grid xs={12} item>
                {/* If we have a message, it's probably important. */}
                {integrationCredential.message ? (
                  <h2>{integrationCredential.message}</h2>
                ) : null}
              </Grid>

              <ForLivestockAgent>
                <Grid item xs={12}>
                  <CheckBoxBase
                    label="Ingest reference invoices and/or RCTIs from Xero"
                    onChange={setIngestExternalDocument}
                    value={integrationCredential.ingestExternalDocuments}
                    tooltip="If checked, reference invoices and/or RCTIs will be created within AgriNous where a business is linked to a Xero business."
                  />
                </Grid>
              </ForLivestockAgent>

              <Grid item xs={9} sm={7} md={5} lg={3}>
                {tenantOptions.length > 1 ||
                !integrationCredential.defaultTenant ? (
                  <ReactSelect
                    defaultValue={tenantOptions.find(
                      tenantOption => tenantOption.value === tenantId,
                    )}
                    options={tenantOptions}
                    placeholder="Select a Tenant..."
                    onChange={option => setTenantId(option?.value || null)}
                  />
                ) : (
                  (tenants || []).find(tenant => tenant.tenantId === tenantId)
                    ?.tenantName ||
                  (tenantId ? "Unknown Tenant selected" : "No Tenant selected")
                )}
              </Grid>
              {tenantOptions.length > 1 && (
                <Grid item xs={3} sm={3} md={3} lg={2}>
                  <Button
                    disabled={
                      !(
                        tenantId &&
                        tenantId !== integrationCredential.defaultTenant
                      )
                    }
                    onClick={updateTenant}
                  >
                    Set Tenant
                  </Button>
                </Grid>
              )}

              <Grid item xs={12}>
                <h4>
                  {integrationCredential.isEnabled ? (
                    <LargeFaIcon
                      icon={faCheckCircle}
                      title="This integration is currently enabled"
                      color="success"
                    />
                  ) : (
                    <LargeFaIcon
                      icon={faExclamationCircle}
                      title="This integration is currently disabled"
                      color="warning"
                    />
                  )}
                  &nbsp; Created by{" "}
                  {integrationCredential.createdBy
                    ? `${integrationCredential.createdBy.firstName} ${integrationCredential.createdBy.lastName}`
                    : "AgriNous Admin"}{" "}
                  on{" "}
                  {formatUTCToLocalDateTimeString(
                    integrationCredential.created,
                  )}
                </h4>
              </Grid>

              <Grid item xs={12}>
                <p>
                  {integrationCredential.integrationBusinessesLastSyncedAt ? (
                    <SmallFaIcon
                      icon={faCheckCircle}
                      title="This integration has synchronised"
                      color="success"
                    />
                  ) : (
                    <SmallFaIcon
                      icon={faExclamationCircle}
                      title="This integration has not yet synchronised"
                      color="warning"
                    />
                  )}
                  &nbsp; Business synced{" "}
                  {integrationCredential.integrationBusinessesLastSyncedAt
                    ? TimeSince({
                        utcDateTimeStr:
                          integrationCredential.integrationBusinessesLastSyncedAt,
                        tooltipPlacement: "right",
                      })
                    : "never"}
                </p>
                <p>
                  {integrationCredential.integrationDocumentsLastSyncedAt ? (
                    <SmallFaIcon
                      icon={faCheckCircle}
                      title="This integration has synchronised documents"
                      color="success"
                    />
                  ) : (
                    <SmallFaIcon
                      icon={faExclamationCircle}
                      title="This integration has not yet synchronised documents"
                      color="warning"
                    />
                  )}
                  &nbsp; Documents synced{" "}
                  {integrationCredential.integrationDocumentsLastSyncedAt
                    ? TimeSince({
                        utcDateTimeStr:
                          integrationCredential.integrationDocumentsLastSyncedAt,
                        tooltipPlacement: "right",
                      })
                    : "never"}
                </p>
              </Grid>
              <Grid
                item
                xs={12}
                container
                justifyContent="flex-end"
                alignItems="center"
              >
                <Button onClick={reactivate}>Reactivate Integration</Button>

                <Button
                  disabled={!integrationCredential.isEnabled}
                  onClick={openSynchronise}
                >
                  Synchronise
                </Button>

                {integrationCredential.isEnabled ? (
                  <div
                    onClick={openConfirmDelete}
                    className="cursor-pointer flex flex-1"
                  >
                    {
                      IntegrationTypeDisconnectMap[
                        integrationCredential.type as IntegrationType
                      ]
                    }
                  </div>
                ) : (
                  <div onClick={reactivate}>
                    <img
                      src={XeroConnect}
                      className="cursor-pointer"
                      width={190}
                      height={43}
                      alt="Connect Xero"
                    />
                  </div>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </PaddedPaper>
      <ConfirmDialog
        title={createModalTitle("this credential")}
        message={confirmMessage}
        isOpen={isConfirmDeleteOpen}
        onCancel={closeConfirmDelete}
        onDelete={onDelete}
        includeDeleteConfirmation
      />
      <SynchroniseModal
        integrationCredentialId={id}
        isOpen={isSynchroniseOpen}
        onClose={closeSynchronise}
      />
    </>
  );
};

const AddXeroIntegrationButton = () => {
  const billingSettingsLookup = useSelector(getBillingSettings) as Record<
    string,
    BillingSettings
  >;
  const integrationCredentialByIdLookup = useSelector(
    getIntegrationCredentials,
  ) as Record<string, IntegrationCredential>;

  const dispatch = useDispatch();

  // Only show these buttons for those with permission
  const hasLinkPermission = useCanLinkExternalIntegrationPermission();
  if (!hasLinkPermission) {
    return null;
  }

  const onAdd = (billingSettingsId: string) => {
    dispatch(
      IntegrationCredentialAction.create(
        {
          billingSettingsId,
          type: IntegrationType.Xero,
        },
        {
          redirectToActivate: true,
        },
      ),
    );
  };

  // Only show buttons those that aren't already integrated into this type.
  const addButtons = Object.values(billingSettingsLookup)
    .filter(
      billingSettings =>
        !billingSettings.xeroIntegrationCredentialId ||
        !integrationCredentialByIdLookup[
          billingSettings.xeroIntegrationCredentialId
        ],
    )
    .map(billingSettings => ({
      onClick: () => onAdd(billingSettings.id),
      title: `Add ${IntegrationType.Xero} integration for ${billingSettings.organizationName}`,
    }));

  if (addButtons.length === 1) {
    return (
      <div onClick={addButtons[0].onClick}>
        <img
          src={XeroConnect}
          className="cursor-pointer"
          width={190}
          height={43}
          alt="Connect Xero"
        />
      </div>
    );
  }

  if (addButtons.length > 0) {
    return (
      <Row justifyEnd>
        <MultiButton buttons={addButtons} />
      </Row>
    );
  }

  return null;
};

const IntegrationTypeConnectMap: Record<IntegrationType, ReactNode> = {
  [IntegrationType.Xero]: <AddXeroIntegrationButton />,
};

interface IntegrationSetProps {
  type: IntegrationType;
}

const IntegrationSet = ({ type }: IntegrationSetProps) => {
  const integrationCredentialIdsByType = useSelector(
    selectIntegrationCredentialIdsByTypeLookup,
  ) as Partial<Record<IntegrationType, string[]>>;

  return (
    <>
      <h1>{type}</h1>
      {IntegrationTypeHelpMap[type]}

      {integrationCredentialIdsByType[type] ? (
        <>
          {integrationCredentialIdsByType[type].map(id => (
            <IntegrationCredentialCard id={id} key={id} />
          ))}
          {IntegrationTypeConnectMap[type]}
        </>
      ) : (
        <MessageBox>
          <Column>{IntegrationTypeConnectMap[type]}</Column>
        </MessageBox>
      )}
    </>
  );
};

export const IntegrationSettings = () => {
  // oauth_callback redirects here
  // request all required resources on page load.
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(BillingSettingsAction.request());
    dispatch(IntegrationCredentialAction.request());
    dispatch(XeroAccountingSettingsAction.request());
  }, [dispatch]);

  return (
    <Wrapper>
      <WaitForSync
        requiredData={[
          ApiModel.BILLING_SETTINGS,
          ApiModel.INTEGRATION_CREDENTIALS,
          ApiModel.XERO_ACCOUNTING_SETTINGS,
        ]}
      >
        {Object.values(IntegrationType).map(type => (
          <IntegrationSet type={type} key={type} />
        ))}
      </WaitForSync>
    </Wrapper>
  );
};
